import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cn, generateInitials } from "@/lib/utils"
import { getSafeAvatarUrl } from "@/utils/avatar"

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full border-2 border-kinky-600/50 shadow-lg shadow-kinky-500/20 transition-all duration-300 hover:border-kinky-500 hover:shadow-kinky-400/30",
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    src={getSafeAvatarUrl(src)}
    {...props}
  />
))
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
    skinTone?: keyof typeof import('../../types').SkinTones
  }
>(({ className, children, skinTone, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-kinky-600 to-kinky-700 text-white font-kinky font-medium text-sm shadow-inner",
      skinTone && `bg-skin-${skinTone}`,
      className
    )}
    {...props}
  >
    {children}
  </AvatarPrimitive.Fallback>
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

// Enhanced Avatar with built-in functionality and body-positive features
interface EnhancedAvatarProps {
  src?: string
  alt?: string
  name?: string
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
  verified?: boolean
  premium?: boolean
  accountType?: "individual" | "couple"
  skinTone?: "porcelain" | "fair" | "light" | "medium-light" | "medium" | "medium-dark" | "dark" | "very-dark" | "deep"
  kinkLevel?: "curious" | "explorer" | "enthusiast" | "expert" | "master"
  bodyPositive?: boolean
  className?: string
  onClick?: () => void
}

const EnhancedAvatar = React.forwardRef<HTMLDivElement, EnhancedAvatarProps>(
  ({ 
    src, 
    alt, 
    name = "", 
    size = "md", 
    verified = false, 
    online = false, 
    premium = false,
    accountType = "individual",
    skinTone,
    kinkLevel,
    bodyPositive = false,
    className,
    onClick 
  }, ref) => {
    const sizeClasses = {
      xs: "h-6 w-6 text-xs",
      sm: "h-8 w-8 text-xs",
      md: "h-10 w-10 text-sm",
      lg: "h-12 w-12 text-base",
      xl: "h-16 w-16 text-lg",
      "2xl": "h-20 w-20 text-xl"
    }

    const borderClass = premium 
      ? "border-luxury-500 ring-2 ring-luxury-400/50" 
      : verified 
        ? "border-kinky-500"
        : bodyPositive
          ? "border-passion-500/70"
          : "border-kinky-600/50"
    
    const kinkLevelColors = {
      curious: "ring-artistic-400/30",
      explorer: "ring-sensual-400/40", 
      enthusiast: "ring-passion-400/50",
      expert: "ring-kinky-400/60",
      master: "ring-luxury-400/70"
    }

    return (
      <div 
        ref={ref} 
        className={cn("relative", onClick && "cursor-pointer", className)}
        onClick={onClick}
      >
        <Avatar className={cn(
          sizeClasses[size],
          borderClass,
          premium && "shadow-lg shadow-luxury-500/20",
          kinkLevel && `ring-2 ${kinkLevelColors[kinkLevel]}`,
          bodyPositive && "animate-sensual-pulse"
        )}>
          <AvatarImage src={src} alt={alt || name} />
          <AvatarFallback 
            skinTone={skinTone}
            className={cn(
              premium && "bg-gradient-to-br from-luxury-600 to-luxury-700",
              bodyPositive && "bg-gradient-to-br from-passion-600 to-passion-700",
              !skinTone && !premium && !bodyPositive && "bg-gradient-to-br from-kinky-600 to-kinky-700"
            )}
          >
            {generateInitials(name)}
          </AvatarFallback>
        </Avatar>
        
        {/* Online indicator */}
        {online && (
          <div className={cn(
            "absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-dark-950 bg-passion-500 animate-pulse",
            size === "xs" ? "h-2 w-2" :
            size === "sm" ? "h-2.5 w-2.5" : 
            size === "lg" ? "h-4 w-4" : 
            size === "xl" ? "h-5 w-5" :
            size === "2xl" ? "h-6 w-6" : "h-3 w-3"
          )} />
        )}
        
        {/* Kinky Verification badge */}
        {verified && (
          <div className={cn(
            "absolute -top-0.5 -right-0.5 rounded-full bg-kinky-600 p-0.5 shadow-lg shadow-kinky-500/50",
            size === "xs" ? "h-3 w-3" :
            size === "sm" ? "h-4 w-4" : 
            size === "lg" ? "h-6 w-6" : 
            size === "xl" ? "h-7 w-7" :
            size === "2xl" ? "h-8 w-8" : "h-5 w-5"
          )}>
            <svg className="h-full w-full text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {/* Luxury Premium crown */}
        {premium && (
          <div className={cn(
            "absolute -top-1 -left-1 text-luxury-500 animate-luxury-shimmer",
            size === "xs" ? "text-xs" :
            size === "sm" ? "text-sm" : 
            size === "lg" ? "text-lg" : 
            size === "xl" ? "text-xl" :
            size === "2xl" ? "text-2xl" : "text-base"
          )}>
            <svg className="w-full h-full drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        )}

        {/* Kinky Couple indicator */}
        {accountType === "couple" && (
          <div className={cn(
            "absolute -bottom-1 -left-1 bg-passion-600 rounded-full p-0.5 shadow-lg shadow-passion-500/30",
            size === "xs" ? "h-3 w-3" :
            size === "sm" ? "h-4 w-4" : 
            size === "lg" ? "h-6 w-6" : 
            size === "xl" ? "h-7 w-7" :
            size === "2xl" ? "h-8 w-8" : "h-5 w-5"
          )}>
            <svg className="h-full w-full text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
          </div>
        )}
        
        {/* Body Positive badge */}
        {bodyPositive && (
          <div className={cn(
            "absolute top-0 left-0 bg-passion-500 rounded-full p-0.5 shadow-lg shadow-passion-500/30 animate-sensual-pulse",
            size === "xs" ? "h-3 w-3" :
            size === "sm" ? "h-4 w-4" : 
            size === "lg" ? "h-6 w-6" : 
            size === "xl" ? "h-7 w-7" :
            size === "2xl" ? "h-8 w-8" : "h-5 w-5"
          )}>
            <svg className="h-full w-full text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
            </svg>
          </div>
        )}
      </div>
    )
  }
)
EnhancedAvatar.displayName = "EnhancedAvatar"

// Couple Avatar Component - Shows two overlapping avatars
interface CoupleAvatarProps {
  primarySrc?: string
  primaryAlt?: string
  primaryName?: string
  secondarySrc?: string
  secondaryAlt?: string
  secondaryName?: string
  size?: "sm" | "md" | "lg" | "xl"
  verified?: boolean
  premium?: boolean
  className?: string
  onClick?: () => void
}

const CoupleAvatar = React.forwardRef<HTMLDivElement, CoupleAvatarProps>(
  ({ 
    primarySrc, 
    primaryAlt, 
    primaryName = "",
    secondarySrc,
    secondaryAlt,
    secondaryName = "",
    size = "md", 
    verified = false, 
    online = false, 
    premium = false,
    className,
    onClick 
  }, ref) => {
    const containerSizes = {
      sm: "h-8 w-12",
      md: "h-10 w-14", 
      lg: "h-12 w-16",
      xl: "h-16 w-20"
    }

    const avatarSizes = {
      sm: "h-7 w-7 text-xs",
      md: "h-9 w-9 text-xs",
      lg: "h-11 w-11 text-sm", 
      xl: "h-14 w-14 text-base"
    }

    return (
      <div 
        ref={ref}
        className={cn("relative", containerSizes[size], onClick && "cursor-pointer", className)}
        onClick={onClick}
      >
        {/* Primary avatar (left) */}
        <Avatar className={cn(avatarSizes[size], "absolute left-0 z-10 border-2", premium ? "border-yellow-500" : "border-dark-700")}>
          <AvatarImage src={primarySrc} alt={primaryAlt || primaryName} />
          <AvatarFallback className={cn(premium && "bg-gradient-to-br from-yellow-600 to-yellow-700")}>
            {generateInitials(primaryName)}
          </AvatarFallback>
        </Avatar>

        {/* Secondary avatar (right, overlapping) */}
        <Avatar className={cn(avatarSizes[size], "absolute right-0 border-2", premium ? "border-yellow-500" : "border-dark-700")}>
          <AvatarImage src={secondarySrc} alt={secondaryAlt || secondaryName} />
          <AvatarFallback className={cn(premium && "bg-gradient-to-br from-accent-600 to-primary-600")}>
            {generateInitials(secondaryName)}
          </AvatarFallback>
        </Avatar>

        
        {/* Verification badge */}
        {verified && (
          <div className={cn(
            "absolute -top-0.5 -right-0.5 rounded-full bg-blue-600 p-0.5 z-20",
            size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : size === "xl" ? "h-7 w-7" : "h-5 w-5"
          )}>
            <svg className="h-full w-full text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {/* Premium crown */}
        {premium && (
          <div className={cn(
            "absolute -top-1 -left-1 text-yellow-500 z-20",
            size === "sm" ? "text-xs" : size === "lg" ? "text-lg" : size === "xl" ? "text-xl" : "text-base"
          )}>
            <svg className="w-full h-full drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        )}
      </div>
    )
  }
)
CoupleAvatar.displayName = "CoupleAvatar"

export { Avatar, AvatarImage, AvatarFallback, EnhancedAvatar, CoupleAvatar }